import { queryKeys, routes, TEXT_STRING } from '../../../constants';
import {
  EGiftPaymentStep,
  EPaymentMethod,
  TBodyGiftRequest,
  TBodyPaymentGift,
  TPaymentPayload,
  TResPaymentGift,
} from 'types';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  getGiftPendingPayment,
  getGiftPredict,
  paymentGift,
  paymentGiftRequest,
} from 'api';
import { useCallback, useContext, useState } from 'react';
import { useToast } from 'hooks';
import { usePostStore } from 'store';
import { isAxiosError } from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChatContext } from 'components/Chat/context';
import MethodPayment from 'components/PaymentMethod/PaymentMethod';
import {
  DraggableModal,
  Button,
  Loading,
  Modal,
  Text,
  CardPayment,
  ProcessingPriceList,
} from 'components';

type Props = {
  step?: EGiftPaymentStep;
  onClose: () => void;
  giftId: number;
  femaleId?: number;
  postId?: number;
  onNextStep: () => void;
  onPreviousStep?: () => void;
  onSendMessage: (data: TResPaymentGift) => void;
  onChooseStep?: (step?: EGiftPaymentStep) => void;
  onSuccess?: () => void;
  onError?: () => void;
  redirect?: string;
};

const titleModal = {
  [EGiftPaymentStep.GIFTS_PREDICT]: TEXT_STRING.COMMON.TOTAL_STAMPS,
  [EGiftPaymentStep.CHOOSE_METHOD]:
    TEXT_STRING.PAYMENT.CHOOSE_PAYMENT_METHOD_PLEASE,
  [EGiftPaymentStep.CHOOSE_CARD]: TEXT_STRING.COMMON.CREDIT_CARD_REGISTRATION,
};

const GiftPaymentStep = ({
  step,
  onClose,
  onNextStep,
  onPreviousStep,
  giftId,
  femaleId,
  postId,
  onSendMessage,
  onChooseStep,
  onSuccess,
  onError,
  redirect,
}: Props) => {
  // Hooks
  const { toastError } = useToast();
  const { historyPaymentId, setHistoryPaymentId, setStepPaymentGift } =
    useContext(ChatContext);
  const { setListPostPaid } = usePostStore();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // States
  const [paymentMethod, setPaymentMethod] = useState<EPaymentMethod>(
    EPaymentMethod.POINT,
  );
  const [open, setOpen] = useState<boolean>(false);
  // Veilpay payment is temporarily unavailable, so this section is commented out.(#1987)
  // const [cardWarningOpen, setCardWarningOpen] = useState<boolean>(false);

  // Query
  const { data, isLoading } = useQuery({
    queryKey: [queryKeys.GIFT_PREDICT, giftId],
    queryFn: () => getGiftPredict({ giftId }),
    enabled: !!giftId && !historyPaymentId,
  });

  const {
    data: pendingGift,
    isLoading: loadingPendingGift,
    isRefetching,
  } = useQuery({
    queryKey: [queryKeys.GIFT_PENDING_PAYMENT, historyPaymentId],
    queryFn: () => getGiftPendingPayment(historyPaymentId || 0),
    enabled: !!historyPaymentId,
  });

  // Mutations
  const { mutateAsync: payGift, isPending: payingGift } = useMutation({
    mutationFn: (body: TBodyPaymentGift) => {
      return paymentGift(body);
    },
    onSuccess(data) {
      const { post, ...res } = data;
      if (data?.tds_auth_url) {
        return window.open(data?.tds_auth_url, '_self');
      }
      if (data?.name) {
        onSendMessage(res);
      }
      if (post) {
        setListPostPaid(post);
        setOpen(true);
      }
      onSuccess && onSuccess();
    },
    onError(error) {
      if (isAxiosError(error)) {
        if (error.message === TEXT_STRING.ERROR.NOT_ENOUGH_POINT) {
          navigate(routes.BUY_POINTS);
        } else if (
          error.response?.data.channelId &&
          error.response?.data.retry === false &&
          !pathname?.includes(error.response?.data.channelId)
        ) {
          navigate(
            routes.CONVERSATION.replace(':id', error.response?.data.channelId),
          );
        }
      }
      onError && onError();
    },
  });

  const { mutateAsync: payGiftPending, isPending: paying } = useMutation({
    mutationFn: (body: TBodyGiftRequest) => {
      return paymentGiftRequest(body);
    },
    onSuccess(data) {
      onSendMessage(data);
    },
  });

  // Memo, callbacks
  const handleClose = useCallback(() => {
    if (!paying && !payingGift) {
      setHistoryPaymentId(undefined);
      setPaymentMethod(EPaymentMethod.POINT);
      onClose();
    }
  }, [onClose, paying, payingGift, setHistoryPaymentId]);

  // Veilpay payment is temporarily unavailable, so this section is commented out.(#1987)
  // const handleCardWarningClose = useCallback(() => {
  //   setCardWarningOpen(false);
  //   navigate(routes.BUY_POINTS);
  // }, [navigate]);

  const handlePayment = useCallback(
    async (payload?: TPaymentPayload) => {
      const { token, customerId } = payload || {};
      if (!token && !customerId && paymentMethod !== EPaymentMethod.POINT)
        return;
      if (historyPaymentId) {
        await payGiftPending({
          token,
          customerId,
          type: payload?.type || paymentMethod,
          historyId: historyPaymentId,
          pendingIds: [],
        });
      } else if (femaleId && giftId) {
        await payGift({
          token,
          customerId,
          femaleId,
          giftId,
          postId,
          amount: data?.amount || 0,
          type: payload?.type || paymentMethod,
          secureReturnUrl:
            payload?.type === EPaymentMethod.VISA
              ? `${window.location.origin}${routes.FINISH_PAYMENT}${
                  redirect ? `?redirect=${encodeURIComponent(redirect)}` : ''
                }`
              : '',
        });
      }
    },
    [
      paymentMethod,
      historyPaymentId,
      femaleId,
      giftId,
      payGiftPending,
      payGift,
      postId,
      data?.amount,
      redirect,
    ],
  );

  const handleSubmitPaymentMethod = useCallback(async () => {
    try {
      let step = undefined;
      if (paymentMethod === EPaymentMethod.POINT) {
        await handlePayment();
      } else {
        // Veilpay payment is temporarily unavailable, so this section is commented out.(#1987)
        // step = EGiftPaymentStep.CHOOSE_CARD;
        // setCardWarningOpen(true);
      }
      setStepPaymentGift(step);
      onChooseStep && onChooseStep(step);
    } catch (error) {
      toastError(error as Error);
    }
  }, [
    handlePayment,
    onChooseStep,
    paymentMethod,
    setStepPaymentGift,
    toastError,
  ]);

  return (
    <>
      <DraggableModal
        onClose={handleClose}
        open={!!step}
        title={titleModal[step || EGiftPaymentStep.GIFTS_PREDICT]}
        className="pb-4"
      >
        {step === EGiftPaymentStep.GIFTS_PREDICT && (
          <ProcessingPriceList
            processing={pendingGift?.pending}
            totalAmount={pendingGift?.totalAmount}
            onNextStep={onNextStep}
            disabled={pendingGift?.isSuccess}
            isRefetching={isRefetching}
            isLoading={loadingPendingGift}
          />
        )}
        {step === EGiftPaymentStep.CHOOSE_METHOD && (
          <MethodPayment
            onChooseMethod={setPaymentMethod}
            onSubmit={handleSubmitPaymentMethod}
            price={pendingGift?.totalAmount || data?.amount || 0}
            paymentMethod={paymentMethod}
            loading={payingGift}
            allowPaymentMethod={
              historyPaymentId
                ? pendingGift?.allowPaymentMethod
                : data?.allowPaymentMethod
            }
          />
        )}
        {step === EGiftPaymentStep.CHOOSE_CARD && (
          <>
            {isLoading ? (
              <Loading />
            ) : (
              <CardPayment
                price={
                  pendingGift?.remainingAmount ||
                  (data?.amount || 0) * (1 + (data?.fee || 0))
                }
                paying={payingGift || paying}
                onPay={handlePayment}
                title=""
                subTitle={TEXT_STRING.CALL_WOMAN.PAYMENT_WARNING}
                onSettled={handleClose}
                onCancel={onPreviousStep}
              />
            )}
          </>
        )}
        {historyPaymentId && (
          <Button
            onClick={() => {
              onPreviousStep && onPreviousStep();
            }}
            bgColor="bg-transparent"
            textColor="text-primary"
            disabled={paying || payingGift}
            padding=""
          >
            {TEXT_STRING.COMMON.RETURN}
          </Button>
        )}
      </DraggableModal>
      <Modal open={open} onClose={() => {}}>
        <div className="w-full flex flex-col gap-4 bg-white p-6">
          <Text center bold>
            {TEXT_STRING.POST.PREMIUM_POST_NOTE}
          </Text>
          <Button onClick={() => setOpen(false)} block bold>
            {TEXT_STRING.COMMON.YES}
          </Button>
        </div>
      </Modal>
      {/* // Veilpay payment is temporarily unavailable, so this section is commented out.(#1987)
        <Alert
          open={cardWarningOpen}
          message={TEXT_STRING.PAYMENT.CARD_MAINTENANCE}
          onClose={handleCardWarningClose}
        /> 
      */}
    </>
  );
};

export default GiftPaymentStep;
